// My Reports — own daily entry. Breathable, color-coded categories, calendar-like
// day cells. Scrolling is fine. Designed to feel alive, not enterprise-form.

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spin, message, Tooltip } from 'antd';
import { CheckCircleFilled, SyncOutlined, ExclamationCircleFilled, CalendarOutlined } from '@ant-design/icons';
import { tokens, styles } from './theme';
import PeriodSelector from './PeriodSelector';
import ActivityForm from './ActivityForm';
import { todayPeriod, dateForCell, formatDmy, DAYS, SHORT_DAYS, parseCounts, computeDayGrandTotal } from './manualReportUtils';
import {
  fetchManualReportRoleConfig,
  fetchMyReportRole,
  fetchMyReports,
  upsertMyReportEntry,
} from '../../../Slices/manualReportsSlice';

const AUTOSAVE_DEBOUNCE_MS = 800;

// ─── week status strip ───────────────────────────────────────────────────
// Shows the 7 days of the selected week with a glance-friendly status:
//   ● green   = logged (entry exists with any activity)
//   ◐ orange  = today (highlight)
//   ○ gray    = past weekday with no entry (missed)
//   — faded   = future day or weekend (not yet expected)
// Click any day to jump the period selector to it.
const WeekStatusStrip = ({ config, period, entries, onJump }) => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const todayDate = new Date();

  const days = DAYS.map((d, i) => {
    const date = dateForCell(period.year, period.monthIndex, period.week, d);
    if (!date) return { d, shortLabel: SHORT_DAYS[i], iso: null, inMonth: false };
    const iso = date.toISOString().slice(0, 10);
    const inMonth = date.getUTCMonth() === period.monthIndex;
    const inFuture = iso > todayIso;
    const isToday = iso === todayIso;
    const isWeekend = i >= 5;
    const entry = entries.find(e =>
      e.year === period.year && e.monthIndex === period.monthIndex &&
      e.week === period.week && e.day === d
    );
    const counts = entry ? parseCounts(entry) : {};
    const hasActivity = computeDayGrandTotal(config, counts) > 0 || (entry?.attendance?.loginTime);
    return {
      d, shortLabel: SHORT_DAYS[i],
      dateNum: date.getUTCDate(),
      iso, inMonth, inFuture, isToday, isWeekend,
      hasEntry: !!entry,
      hasActivity,
    };
  });

  const weekdays = days.filter(x => x.inMonth && !x.isWeekend);
  const loggedWeekdays = weekdays.filter(x => x.hasActivity).length;
  const totalWeekdays = weekdays.length;
  const missingPast = weekdays.filter(x => !x.hasActivity && !x.inFuture && !x.isToday).length;

  const cellColor = (cell) => {
    if (!cell.inMonth) return { bg: 'transparent',         dot: '#cbd5e1', text: tokens.color.textFaint, label: 'Not in month' };
    if (cell.isToday)  return { bg: '#fff7ed',             dot: '#f97316', text: '#c2410c',              label: cell.hasActivity ? 'Today — logged' : 'Today — not yet' };
    if (cell.hasActivity) return { bg: tokens.color.successBg, dot: tokens.color.success, text: tokens.color.success, label: 'Logged' };
    if (cell.inFuture) return { bg: tokens.color.surfaceAlt, dot: '#cbd5e1', text: tokens.color.textFaint, label: 'Upcoming' };
    if (cell.isWeekend) return { bg: tokens.color.surfaceAlt, dot: '#cbd5e1', text: tokens.color.textFaint, label: 'Weekend' };
    return { bg: '#fef2f2', dot: '#f87171', text: '#dc2626', label: 'Missed' };
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      alignItems: 'flex-end',
    }}>
      {/* Summary line */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.space.sm,
        fontSize: 12,
      }}>
        <span style={{ color: tokens.color.textMuted, fontWeight: 500 }}>This week</span>
        <span style={{
          padding: '2px 10px',
          borderRadius: tokens.radius.pill,
          background: loggedWeekdays === totalWeekdays && totalWeekdays > 0 ? tokens.color.successBg : tokens.color.surfaceAlt,
          color: loggedWeekdays === totalWeekdays && totalWeekdays > 0 ? tokens.color.success : tokens.color.text,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {loggedWeekdays} / {totalWeekdays} weekdays logged
        </span>
        {missingPast > 0 && (
          <span style={{
            padding: '2px 10px',
            borderRadius: tokens.radius.pill,
            background: '#fef2f2', color: '#dc2626',
            fontWeight: 600,
            fontSize: 11,
          }}>
            {missingPast} missed
          </span>
        )}
      </div>

      {/* 7-day strip */}
      <div style={{ display: 'flex', gap: 6 }}>
        {days.map((cell) => {
          const c = cellColor(cell);
          const selected = cell.d === period.day;
          return (
            <Tooltip key={cell.d} title={cell.iso ? `${cell.d}, ${cell.iso} — ${c.label}` : cell.d}>
              <button
                type="button"
                onClick={() => cell.inMonth && onJump(cell.d)}
                disabled={!cell.inMonth}
                style={{
                  width: 44,
                  height: 56,
                  padding: '6px 4px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  background: c.bg,
                  border: `1px solid ${selected && cell.inMonth ? tokens.color.accent : tokens.color.border}`,
                  borderRadius: tokens.radius.md,
                  cursor: cell.inMonth ? 'pointer' : 'not-allowed',
                  opacity: cell.inMonth ? 1 : 0.4,
                  transition: `all ${tokens.duration.fast}`,
                  boxShadow: selected && cell.inMonth ? `0 0 0 2px ${tokens.color.accentBg}` : 'none',
                }}
              >
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.6, color: c.text }}>
                  {cell.shortLabel}
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, color: c.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                  {cell.inMonth ? cell.dateNum : '—'}
                </span>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%', background: c.dot,
                }} />
              </button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

// ─── save status ──────────────────────────────────────────────────────────
const SaveStatus = ({ status, lastSavedAt }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (status !== 'saved') return;
    const i = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(i);
  }, [status]);

  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: tokens.radius.pill,
    fontSize: 12,
    fontWeight: 600,
  };
  if (status === 'saving') return (
    <span style={{ ...base, background: tokens.color.surfaceAlt, color: tokens.color.textMuted, border: `1px solid ${tokens.color.border}` }}>
      <SyncOutlined spin /> Saving
    </span>
  );
  if (status === 'saved' && lastSavedAt) {
    const secs = Math.floor((Date.now() - lastSavedAt) / 1000);
    const label = secs < 5 ? 'Saved' : secs < 60 ? `Saved ${secs}s ago` : `Saved ${Math.floor(secs/60)}m ago`;
    return (
      <span style={{ ...base, background: tokens.color.successBg, color: tokens.color.success, border: `1px solid ${tokens.color.successBorder}` }}>
        <CheckCircleFilled /> {label}
      </span>
    );
  }
  if (status === 'error') return (
    <span style={{ ...base, background: tokens.color.dangerBg, color: tokens.color.danger, border: `1px solid ${tokens.color.danger}` }}>
      <ExclamationCircleFilled /> Save failed
    </span>
  );
  return null;
};

// ─── page ─────────────────────────────────────────────────────────────────
export default function MyReportsPage() {
  const dispatch = useDispatch();
  const config = useSelector(s => s.manualReports.config);
  const myRole = useSelector(s => s.manualReports.myRole);
  const myReports = useSelector(s => s.manualReports.myReports);

  const [period, setPeriod] = useState(todayPeriod());
  const [draft, setDraft] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!config) dispatch(fetchManualReportRoleConfig());
    if (!myRole) dispatch(fetchMyReportRole());
  }, [dispatch, config, myRole]);

  useEffect(() => {
    dispatch(fetchMyReports({ year: period.year, monthIndex: period.monthIndex }));
  }, [dispatch, period.year, period.monthIndex]);

  const currentEntry = useMemo(() =>
    myReports.find(e =>
      e.year === period.year && e.monthIndex === period.monthIndex &&
      e.week === period.week && e.day === period.day
    ),
  [myReports, period]);

  useEffect(() => {
    setDraft(currentEntry || { attendance: {}, counts: {}, leadIds: '', remarks: '', meetings: '', complaints: '' });
    setSaveStatus('idle');
  }, [period.year, period.monthIndex, period.week, period.day, currentEntry?.id]);

  const handleDraftChange = (next) => {
    setDraft(next);
    setSaveStatus('saving');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await dispatch(upsertMyReportEntry({
          year: period.year, monthIndex: period.monthIndex,
          week: period.week, day: period.day,
          attendance: next.attendance,
          counts: next.counts,
          leadIds: next.leadIds,
          remarks: next.remarks,
          meetings: next.meetings,
          complaints: next.complaints,
        })).unwrap();
        setSaveStatus('saved');
        setLastSavedAt(Date.now());
      } catch (err) {
        setSaveStatus('error');
        message.error(err || 'Failed to save');
      }
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  const dateForCurrentDay = dateForCell(period.year, period.monthIndex, period.week, period.day);
  const dateStr = dateForCurrentDay ? formatDmy(dateForCurrentDay) : '—';

  if (!config || !myRole) {
    return (
      <div style={{ ...styles.page, textAlign: 'center', paddingTop: tokens.space.xxxxl }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ ...styles.pageInner, gap: tokens.space.lg }}>

        {/* HEADER — subtle gradient panel with title, role chip, date, save status */}
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
          border: `1px solid ${tokens.color.border}`,
          borderRadius: tokens.radius.lg,
          padding: tokens.space.xl,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space.lg,
          flexWrap: 'wrap',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute',
            top: -40, right: -20,
            width: 160, height: 160,
            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.08), transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none',
          }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{
              ...tokens.type.pageTitle,
              margin: 0,
              color: tokens.color.textStrong,
            }}>My Daily Report</h1>
            <div style={{
              ...tokens.type.body,
              color: tokens.color.textMuted,
              marginTop: 6,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <CalendarOutlined style={{ fontSize: 12 }} />
              {period.day}, <span style={tokens.type.mono}>{dateStr}</span>
              <span style={{ color: tokens.color.textFaint, margin: '0 6px' }}>·</span>
              <span style={{ fontSize: 11, color: tokens.color.textFaint }}>
                {myRole?.employeeName || 'your report'}
              </span>
            </div>
          </div>
          {/* Right side — week status + save indicator */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space.sm, alignItems: 'flex-end' }}>
            <SaveStatus status={saveStatus} lastSavedAt={lastSavedAt} />
            <WeekStatusStrip
              config={config}
              period={period}
              entries={myReports}
              onJump={(d) => setPeriod({ ...period, day: d })}
            />
          </div>
        </div>

        {/* PERIOD — calendar-like day cells */}
        <div style={{
          background: tokens.color.surface,
          border: `1px solid ${tokens.color.border}`,
          borderRadius: tokens.radius.lg,
          padding: tokens.space.lg,
        }}>
          <PeriodSelector
            year={period.year}
            monthIndex={period.monthIndex}
            week={period.week}
            day={period.day}
            onChange={setPeriod}
          />
        </div>

        {/* FORM */}
        <ActivityForm
          config={config}
          entry={draft || { attendance: {}, counts: {}, leadIds: '', remarks: '', meetings: '', complaints: '' }}
          onChange={handleDraftChange}
        />
      </div>
    </div>
  );
}
